import React, { memo, useEffect, useRef, useState } from '../../lib/teact/teact';

import type { ApiWalletCounterpartyAvatar, ApiWalletTransactionCounterparty } from '../../api/types';

import buildClassName from '../../util/buildClassName';
import { getMetadataAvatarContentType } from '../../util/metadataAvatar';
import { WALLET_METADATA_DIRECTORY_CHANGED_EVENT } from '../../util/walletMetadataDirectory';
import { isWalletSystemCounterparty } from '../../util/walletSystemMetadata';
import { callApiWithThrow } from '../../api';
import { getAvatarInitials } from '../ui/helpers/getAvatarInitials';

import styles from './MetadataIdentity.module.scss';

import legendsLogo from '../../assets/legendsEnergyLogo.svg';
import fileIcons from '../../assets/transaction-file-icons.svg';

type Props = {
  counterparty: ApiWalletTransactionCounterparty;
  size?: 'small' | 'large';
  className?: string;
  previewUrl?: string;
  isPlaceholder?: boolean;
};

type CachedAvatar = { request: Promise<string | undefined>; url?: string; references: number };
const avatars = new Map<string, CachedAvatar>();
const MAX_CACHED_AVATARS = 80;
const RETRY_DELAYS = [500, 1_500];

function evictAvatars() {
  for (const [key, entry] of avatars) {
    if (avatars.size <= MAX_CACHED_AVATARS) break;
    if (entry.references || !entry.url) continue;
    URL.revokeObjectURL(entry.url);
    avatars.delete(key);
  }
}

export function primeCounterpartyAvatar(
  counterparty: ApiWalletTransactionCounterparty, avatar: ApiWalletCounterpartyAvatar,
) {
  const { account_id: accountId, id, avatar_version: version } = counterparty;
  if (!accountId || !version) return;
  for (const size of ['small', 'large']) {
    const key = `${accountId}:${id}:${version}:${size}`;
    if (avatars.get(key)?.url) continue;
    const url = URL.createObjectURL(new Blob([avatar.content], { type: avatar.content_type }));
    avatars.set(key, { url, request: Promise.resolve(url), references: 0 });
  }
  evictAvatars();
}

function CounterpartyAvatar({ counterparty, size = 'small', className, previewUrl, isPlaceholder }: Props) {
  const { id, name, account_id: accountId, avatar_version: version } = counterparty;
  const isSystemCounterparty = isWalletSystemCounterparty(counterparty);
  const identity = `${accountId}:${id}:${size}`;
  const key = `${accountId}:${id}:${version}:${size}`;
  const [image, setImage] = useState<{ identity: string; key: string; url?: string }>(() => (
    { identity, key, url: avatars.get(key)?.url }
  ));
  const [retry, setRetry] = useState(0);
  const retryRef = useRef({ key, attempts: 0 });
  useEffect(() => {
    const refresh = () => {
      retryRef.current.attempts = 0;
      setRetry((value) => value + 1);
    };
    window.addEventListener(WALLET_METADATA_DIRECTORY_CHANGED_EVENT, refresh);
    window.addEventListener('online', refresh);
    window.addEventListener('focus', refresh);
    return () => {
      window.removeEventListener(WALLET_METADATA_DIRECTORY_CHANGED_EVENT, refresh);
      window.removeEventListener('online', refresh);
      window.removeEventListener('focus', refresh);
    };
  }, []);

  useEffect(() => {
    if (isSystemCounterparty || previewUrl || !accountId || !version) return undefined;
    if (retryRef.current.key !== key) retryRef.current = { key, attempts: 0 };
    let entry = avatars.get(key);
    if (!entry) {
      const nextEntry: CachedAvatar = {
        references: 0,
        request: callApiWithThrow('fetchWalletCounterpartyAvatar', accountId, id, size, version)
          .then((content) => {
            if (!content) return undefined;
            nextEntry.url = URL.createObjectURL(new Blob([content], {
              type: getMetadataAvatarContentType(content),
            }));
            evictAvatars();
            return nextEntry.url;
          }).catch(() => undefined),
      };
      entry = nextEntry;
      avatars.set(key, entry);
    }
    entry.references += 1;
    let isCurrent = true;
    let retryTimer: number | undefined;
    void entry.request.then((url) => {
      if (!url && avatars.get(key) === entry) avatars.delete(key);
      if (!isCurrent) return;
      if (url) {
        retryRef.current.attempts = 0;
        setImage({ identity, key, url });
      } else if (retryRef.current.attempts < RETRY_DELAYS.length) {
        const delay = RETRY_DELAYS[retryRef.current.attempts++];
        retryTimer = window.setTimeout(() => setRetry((value) => value + 1), delay);
      }
    });
    return () => {
      isCurrent = false;
      if (retryTimer) window.clearTimeout(retryTimer);
      entry.references -= 1;
      evictAvatars();
    };
  }, [identity, key, accountId, id, size, version, previewUrl, retry, isSystemCounterparty]);

  const url = isSystemCounterparty ? legendsLogo : previewUrl
    || (version && image.identity === identity ? image.url : undefined);
  return (
    <span
      className={buildClassName(styles.avatar, size === 'large' && styles.avatarLarge, className)}
      data-telemetry-block=""
      aria-hidden
    >
      {isPlaceholder || !name.trim() ? (
        <svg
          className={styles.photoPlaceholder}
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden
        >
          <use href={`${fileIcons}#file-image`} />
        </svg>
      ) : getAvatarInitials(name)}
      {url && <img src={url} alt="" onError={() => setImage({ identity, key })} />}
    </span>
  );
}

export default memo(CounterpartyAvatar);
